<template>
  <div id="pd-analytics-modal-parent">
    <Page title="PD分析">
      <template v-slot:action>
        <ActionButton title="PD分析とは" color="primary" shape="round" width="150" @click="showTutorial" />
      </template>
      <template v-slot:content>
        <div class="uk-card uk-card-default uk-card-body" :class="{ 'main-hidden': activePath === 'sub' }">
          <div class="content-header">
            <ActionButton
              title="分析対象追加"
              color="primary"
              width="150"
              @click="onAddButtonClick"
              :uk-tooltip="`title: 分析可能人数は${participantsMin}人〜${participantsMax}人です。; pos: top-left;`"
            />
            <ActionMenu
              title="CSV出力"
              color="primary"
              shape="round"
              width="150"
              :items="menuItems"
              @item-click="onCsvButtonClick"
              :uk-tooltip="`title: CSVに退職者は含まれません。; pos: top-left;`"
            />
          </div>
          <div class="content-main">
            <div class="selected-users">
              <UserSelector :users="selectedUsers" :readOnly="true" :selectable="true" @user-click="onUserSelect" />
            </div>
            <div class="charts uk-text-center uk-grid uk-grid-medium" ref="chartRef" :class="{ collapse: stats.length < 3 }">
              <div
                class="chart"
                v-for="item in stats"
                :key="item.id"
                :style="{ width: `${item.size}px`, height: `${item.size}px` }"
                @click="onChartClick(item)"
              >
                <div>{{ item.name }}</div>
                <PDAnalyticsChart :stats="item" :size="item.size" zoom="out" />
              </div>
            </div>
          </div>
        </div>
        <div class="uk-flex-top" uk-modal ref="selectionModal" container="#pd-analytics-modal-parent" :bg-close="false">
          <div class="uk-modal-dialog uk-margin-auto-vertical">
            <div class="uk-modal-body">
              <DepartmentUserSelector
                :key="departmentUserSelectorFormKey"
                :full="true"
                :analysisFlg="true"
                :includeRetired="true"
                :initialSelectedUsers="selectedUsers"
                :participantsMax="participantsMax"
                :participantsMin="participantsMin"
                @user-selection-change="onTargetUserSelectionChange"
                @on-selection-done-button-click="onSelectionDoneButtonClick"
                @close="onCloseButtonClick"
                v-on:keydown.esc="onCloseButtonClick"
              />
            </div>
          </div>
        </div>
        <div
          class="uk-flex-top pd-analytics-modal"
          uk-modal
          ref="expansionRef"
          container="#pd-analytics-modal-parent"
          :bg-close="false"
        >
          <div class="uk-modal-dialog uk-margin-auto-vertical">
            <div class="uk-modal-header">
              <h2 class="uk-modal-title">{{ focusedUser.name }}</h2>
              <div class="chart-action-button">
                <ActionButton title="印刷" @click="onPrintButtonClick" />
              </div>
            </div>
            <div class="uk-modal-body">
              <PDAnalyticsChart :stats="focusedUser" :size="getExpandedSize()" :expand="true" :zoom="zoom" />
            </div>
            <div class="uk-modal-footer uk-text-right">
              <ActionButton
                v-if="zoom === 'in'"
                class="zoom-button"
                title="ズームアウト"
                @click="onZoomButtonClick('out')"
                color="primary"
              />
              <ActionButton v-else class="zoom-button" title="ズームイン" @click="onZoomButtonClick('in')" color="primary" />
              <ActionButton title="戻る" class="uk-modal-close" toggleTarget=".pd-analytics-modal" />
            </div>
          </div>
        </div>
      </template>
    </Page>
  </div>
</template>
<style lang="scss" scoped>
.uk-modal-header {
  border-bottom: 0;
}
.chart-action-button {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
.action .action-button:first-child {
  margin-right: $spacing-medium;
}
.zoom-button {
  margin-right: $spacing-small;
  width: 150px;
}
.uk-card-body {
  .content-main {
    display: flex;
  }
  .content-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $spacing-ex-small;
  }
}
.chart {
  border: 1px $chart-grid-color solid;
  text-align: left;
  padding: $spacing-small;
  margin: $spacing-ex-small;
  cursor: pointer;
}
.charts {
  margin-left: 0;
  width: 100%;
  justify-content: normal;
  &.collapse {
    justify-content: start;
    .chart {
      margin-right: $spacing-ex-small;
    }
  }
}
.selected-users {
  width: 250px;
  min-width: 250px;
  margin-right: $spacing-medium;
  max-height: 500px;
  min-height: 300px;
  .user-selector-container {
    height: 100%;
  }
}

.uk-modal-footer {
  border: 0;
}
.uk-modal-dialog {
  width: unset;
  min-width: 960px;
}
.main-hidden {
  display: none;
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import { Ref } from "vue-property-decorator";
import Page from "../molecules/Page.vue";
import ActionButton from "../atoms/ActionButton.vue";
import ActionMenu from "../molecules/ActionMenu.vue";
import UserSelector from "../molecules/UserSelector.vue";
import DepartmentUserSelector from "../organisms/DepartmentUserSelector.vue";
import PDAnalyticsChart from "../atoms/PDAnalyticsChart.vue";
import { SelectableProfile, UserPDStats, LabelAndValue } from "../../types";
import { StatsItemEnum, SystemApi } from "../../autogen";
import UIkit from "uikit";
import { print } from "../../helpers/view-utils";
import ChartViewMixin from "../../helpers/mixins/chart-view";
import Store from "../../store";
import { InstructionLevel } from "../../types";
interface PdAnalyticsCSVMenu extends LabelAndValue {
  type: StatsItemEnum;
}
[];
type menuItem = {
  label: string;
  value: string;
  type: StatsItemEnum;
};
@Options({
  components: {
    Page,
    ActionButton,
    ActionMenu,
    UserSelector,
    DepartmentUserSelector,
    PDAnalyticsChart
  }
})
export default class PDAnalytics extends ChartViewMixin {
  selectedUsers: SelectableProfile[] = [];
  stats: (UserPDStats & { size: number })[] = [];
  activePath = "main";
  focusedUser: Partial<UserPDStats> = {};
  zoom = "out";
  targetStats = StatsItemEnum.PdAnalytics;
  participantsMin = 2;
  participantsMax = 50;
  loadingFunction = true;
  menuItems: menuItem[] = [
    {
      label: "性格・パーソナリティ",
      value: "static",
      type: StatsItemEnum.PdAnalyticsStatic
    },
    {
      label: "社会性・欲求",
      value: "dynamic",
      type: StatsItemEnum.PdAnalyticsDynamic
    }
  ];
  chartRef?: HTMLElement;
  expansionRef?: HTMLElement;
  departmentUserSelectorFormKey = 0;
  tutorialImagePath = require("@/assets/image/PDTutorial.png");

  mounted() {
    this.$nextTick(() => {
      this.chartRef = this.$refs.chartRef as HTMLElement;
      this.expansionRef = this.$refs.expansionRef as HTMLElement;
    });
  }

  async onSelectionDone() {
    this.stats = this.selectedUsers.map(selectedUser => ({
      id: selectedUser.id,
      name: `${selectedUser.lastName} ${selectedUser.firstName}`,
      department: selectedUser.department,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      axis: this.chartData![selectedUser.id].stats[StatsItemEnum.PdAnalytics]!,
      size: this.chartRef ? this.chartRef.clientWidth / 3.5 : 200
    }));
  }
  onUserSelect(selectedUser: SelectableProfile) {
    this.focusedUser = selectedUser;
  }
  async onCsvButtonClick(item: PdAnalyticsCSVMenu) {
    if (this.selectedUsers.length === 0) {
      Store.getInstance().setInstructionContent(
        InstructionLevel.Confirm,
        `全データが出力されますが、よろしいですか？\nデータが多いと時間がかかる場合があります。`,
        async () => {
          try {
            this.downLoadCsvFile(item, ["all"]);
          } catch (err) {
            Store.getInstance().setInstructionContent(
              InstructionLevel.Alert,
              "エラーが発生しました。時間を置いて再度試すか、窓口までお問い合わせください。"
            );
          }
        }
      );
    } else {
      const Employees = this.selectedUsers.map(user => user.id);
      this.downLoadCsvFile(item, Employees);
    }
  }
  private async downLoadCsvFile(menuItem: menuItem, Employees: string[]) {
    Store.getInstance().setIsLoading(true);

    const encoding = require("encoding-japanese");
    try {
      const responseStatic = await new SystemApi().printCsv({
        type: menuItem.type,
        peopleIds: Employees
      });

      const str_array = encoding.stringToCode(responseStatic.data);
      const sjis_array = encoding.convert(str_array, "SJIS", "UNICODE");
      const uint8_array = new Uint8Array(sjis_array);

      const url = window.URL.createObjectURL(new Blob([uint8_array], { type: "text/csv;charset=Shift_JIS" }));
      const anchor = document.createElement("a");
      document.body.appendChild(anchor);
      anchor.setAttribute("href", url);
      anchor.setAttribute("download", `PD分析_${menuItem.label}_${new Date().toISOString()}.csv`);
      anchor.click();
      anchor.remove();
      document.body.onfocus = null;
    } catch (err) {
      let errMessage = "エラーが発生しました。時間を置いて再度試すか、窓口までお問い合わせください。";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
  onChartClick(item: UserPDStats) {
    this.zoom = "out";
    this.focusedUser = item;
    if (this.expansionRef) {
      UIkit.modal(this.expansionRef).show();
    }
  }
  getExpandedSize() {
    return window.innerHeight * 0.7;
  }
  onZoomButtonClick(inOut: "in" | "out") {
    this.zoom = inOut;
  }
  onPrintButtonClick() {
    print(StatsItemEnum.PdAnalytics, this.focusedUser, { zoom: this.zoom });
  }
}
</script>
